/* eslint no-param-reassign: 0 */
import {
  DirectoryNameConsts,
  DirectoryScopes,
  DirectoryTypeConsts,
  DirectoryTypeKeys,
  ItemTypeDisplayNames,
} from 'client/util/Constants';
import { parallelLimit, series } from 'async';
import GSuiteApi from 'client/api/v2/GSuiteApi';
import O365Api from 'client/api/v2/O365Api';
import TranslationRuleApi from 'client/api/v2/TranslationRuleApi';
import Vue from 'vue';
import createModel from 'client/store/modules/models/createModel';

const { gSuite, office365 } = DirectoryTypeKeys;

export const getApi = directoryType => (directoryType === gSuite ? GSuiteApi : O365Api);

export const getDefaultItem = directoryType => () => ({
  directoryType,
  groupsEnabled: false,
  id: null,
  name: '',
  newlyAddedDomain: { updatedDomainSelection: '' },
  defaultDomainIndex: null,
});

/**
 * This function returns a new Vuex module that is used to model a directory in
 * an aside panel. It is used for both Google and Microsoft 365 directories in
 * the Directory Aside Panel.
 */
export default function createDirectoryModel(
  directoryType,
  getDefaultExportAttributes = () => ({}),
  exportAttributesKbUrl,
) {
  const model = createModel(getDefaultItem(directoryType), 'id', getApi(directoryType));

  return {
    namespaced: true,

    state: {
      ...model.state,
      exportAttributes: getDefaultExportAttributes(),
      previousExportAttributes: getDefaultExportAttributes(),
      scopes: [],
      previousGroupsEnabled: false,
      domainAdded: false,
    },

    actions: {
      ...model.actions,

      async fetchItem(context) {
        const { dispatch, getters = {} } = context;

        await model.actions.fetchItem(context);

        if (getters.id) {
          return dispatch('fetchExportAttributes');
        }

        return Promise.resolve();
      },

      fetchExportAttributes({ commit, state }) {
        const { id } = state.item;
        return TranslationRuleApi
          .fetch(directoryType, id)
          .then((attributes) => {
            attributes.forEach((attribute) => {
              const attributeId = attribute.id;
              const attributeName = attribute.builtIn;
              const { direction } = attribute;
              const payload = {
                key: attributeName,
                value: true,
                id: attributeId,
                direction,
              };

              commit('SET_EXPORT_ATTRIBUTE', { ...payload });
              commit('SET_PREVIOUS_EXPORT_ATTRIBUTE', { ...payload });
            });
          });
      },

      async saveDomains(context) {
        const { commit, state } = context;
        const {
          id, newlyAddedDomain, defaultDomainIndex,
        } = state.item;
        const { previousItem } = state;
        const api = getApi(directoryType);
        let previouslyConfiguredDomains = {};
        let updatedSelectedDomains = [];
        let updatedNonConfiguredDomains = [];
        let newDefaultDomain = '';
        let defaultDomainId;
        let payload;
        let oldDefaultDomain;

        await api.listSelectedDomains(id).then((data) => {
          previouslyConfiguredDomains = data.data.domains.map(domain => ({
            name: domain.domain,
            value: domain.domain,
            isDefault: domain.default,
            objectId: domain.objectId,
          }));
        });

        // Filtering the domains which have been added by finding difference
        // between value before modification and after modification of domains
        if (newlyAddedDomain?.updatedDomainSelection) {
          updatedSelectedDomains = newlyAddedDomain.updatedDomainSelection.filter(
            (x) => previouslyConfiguredDomains.map((val) => val.value).indexOf(x.value) < 0,
          );

          // Find domains which were available and deleted by current changes
          updatedNonConfiguredDomains = previouslyConfiguredDomains.filter(
            object1 => !newlyAddedDomain.updatedDomainSelection.some(
              object2 => object1.value === object2.value,
            ),
          );

          newDefaultDomain = newlyAddedDomain?.updatedDomainSelection?.find(
            (x) => x.isDefault === true,
          );

          oldDefaultDomain = previouslyConfiguredDomains?.find((x) => x.isDefault === true);

          if (newDefaultDomain?.objectId
             && oldDefaultDomain?.objectId !== newDefaultDomain?.objectId) {
            payload = {
              defaultDomain: {
                id: newDefaultDomain.objectId,
              },
            };
          }
          if (defaultDomainIndex === -1) {
            payload = {
              defaultDomain: {
                id: '',
              },
            };
          }
        }

        return new Promise((resolve, reject) => {
          const seriesTasks = [];
          if (updatedNonConfiguredDomains.length > 0) {
            updatedNonConfiguredDomains.forEach((removedDomain) => {
              seriesTasks.push(cb => api.deleteDomain(id, removedDomain.objectId)
                .then(() => cb())
                .catch(error => reject(error)));
            });
          }

          updatedSelectedDomains.forEach((selectedDomain) => {
            if (selectedDomain.value) {
              seriesTasks.push(cb => api.saveDomains(id, { domain: selectedDomain.value })
                .then((item) => {
                  if (newDefaultDomain && !defaultDomainId
                    && newDefaultDomain.value === item.data.domain.domain) {
                    defaultDomainId = item.data.domain.objectId;
                  }
                  if (previousItem.defaultDomainIndex?.id !== defaultDomainId) {
                    payload = {
                      defaultDomain: {
                        id: defaultDomainId,
                      },
                    };
                  }
                  commit('SET_ITEM', item);
                  commit('SET_PREVIOUS_ITEM', item);
                  commit('SET_ITEM_STATE', { update: true });
                  return cb();
                }).catch(error => reject(error)));
            }
          });
          seriesTasks.push(cb => api
            .patch(id, payload)
            .then((item) => {
              commit('SET_ITEM', item);
              commit('SET_PREVIOUS_ITEM', item);
              commit('SET_ITEM_STATE', { update: true });
              return cb();
            }).catch(error => reject(error)));
          series(seriesTasks, () => resolve());
        });
      },

      saveExportAttributes({ commit, state }) {
        return new Promise((resolve) => {
          const { id } = state.item;
          const { exportAttributes, previousExportAttributes } = state;
          const tasks = [];
          // For changing direction, we need the tasks to be async
          const seriesTasks = [];

          Object.keys(exportAttributes).forEach((key) => {
            const exportAttribute = exportAttributes[key];
            const previousExportAttribute = previousExportAttributes[key];
            if (exportAttribute.value === true && previousExportAttribute.value === false) {
              tasks.push(cb => TranslationRuleApi
                .create(directoryType, id, key, exportAttribute.direction)
                .then((res) => {
                  // Updating Previous State to keep track of changes
                  // while aside is still open
                  commit('SET_PREVIOUS_EXPORT_ATTRIBUTE', { ...exportAttribute });
                  // Updating State after create with new ID
                  commit('SET_EXPORT_ATTRIBUTE', { ...exportAttribute, id: res.id });
                  return cb();
                })
                .catch(() => cb()));
            } else if (exportAttribute.value === false && previousExportAttribute.value === true) {
              tasks.push(cb => TranslationRuleApi
                .delete(directoryType, id, exportAttribute.id)
                .then(() => {
                  // Updating Previous State to keep track of changes
                  // while aside is still open
                  commit('SET_PREVIOUS_EXPORT_ATTRIBUTE', { ...exportAttribute });
                  // Removing ID to avoid conflicts
                  commit('SET_EXPORT_ATTRIBUTE', { ...exportAttribute, id: null });
                  return cb();
                })
                .catch(() => cb()));
            } else if ((exportAttribute.value === true && previousExportAttribute.value === true)
              && (exportAttribute.direction !== previousExportAttribute.direction)) {
              // Change Attribute Direction (Delete, then create with new direction)
              seriesTasks.push(cb => TranslationRuleApi
                .delete(directoryType, id, exportAttribute.id)
                .then(() => cb())
                .catch(() => cb()));
              seriesTasks.push(cb => TranslationRuleApi
                .create(directoryType, id, key, exportAttribute.direction)
                .then((res) => {
                  // Updating Previous State to keep track of changes
                  // while aside is still open
                  commit('SET_PREVIOUS_EXPORT_ATTRIBUTE', { ...exportAttribute });
                  // Updating State after create with new ID
                  commit('SET_EXPORT_ATTRIBUTE', { ...exportAttribute, id: res.id });
                  return cb();
                })
                .catch(() => cb()));
            }
          });

          parallelLimit(tasks, 5, () => resolve());
          series(seriesTasks, () => resolve());
        });
      },

      // Save groups on modification of groups field
      saveGroups(context, { itemId, name, groups }) {
        const { commit, state } = context;
        const { previousItem } = state;
        const api = getApi(directoryType);
        return api
          .patch(itemId, { name, groupsEnabled: groups })
          .then((item) => {
            if (item.name !== name) {
              throw new Error('Unable to update the name.');
            } else {
              commit('SET_PREVIOUS_GROUPSENABLED', previousItem.groupsEnabled);
              commit('SET_ITEM', item);
              commit('SET_PREVIOUS_ITEM', item);
              commit('SET_ITEM_STATE', { update: true });
            }
          });
      },

      saveItem(context) {
        const { commit, dispatch, state } = context;
        const { id, groupsEnabled, name } = state.item;
        const { previousItem } = state;
        const api = getApi(directoryType);
        const payload = { name };

        // Only include groupsEnabled in patch request if the value has changed
        if (groupsEnabled !== previousItem.groupsEnabled) {
          payload.groupsEnabled = groupsEnabled;
        }

        return api
          .patch(id, payload)
          .then((item) => {
            if (item.name !== name) {
              throw new Error('Unable to update the name.');
            } else {
              commit('SET_ITEM', item);
              commit('SET_PREVIOUS_ITEM', item);
              commit('SET_ITEM_STATE', { update: true });
              dispatch('DirectoryCollection/refreshCollection', { item }, { root: true });
            }
          });
      },

      setDefaultDomainAttribute({ commit }, defaultDomainIndex) {
        commit('SET_DEFAULT_DOMAIN_ATTRIBUTE', defaultDomainIndex);
      },

      setDomainAttribute({ commit }, { updatedDomainSelection }) {
        commit('SET_DOMAIN_ATTRIBUTE', { updatedDomainSelection });
      },

      setDomainEnabled({ commit }, domainAdded) {
        commit('SET_DOMAIN_ENABLED', domainAdded);
      },

      setExportAttribute({ commit }, {
        key, value, id, direction,
      }) {
        commit('SET_EXPORT_ATTRIBUTE', {
          key, value, id, direction,
        });
      },

      setPreviousGroupField({ commit }, groupsEnabled) {
        commit('SET_PREVIOUS_GROUPSENABLED', groupsEnabled);
      },

      setScopes({ commit }, scopes) {
        commit('SET_SCOPES', scopes);
        window.sessionStorage.setItem('scopes', JSON.stringify(scopes));
      },

      setState({ commit }, currentState) {
        commit('SET_STATE', currentState);
      },
    },

    getters: {
      ...model.getters,

      directoryScopes(state) {
        let { scopes } = state;
        // User scope should be added by default
        if (!scopes) {
          scopes = [DirectoryScopes.user];
        }

        if (scopes?.indexOf(DirectoryScopes.user) === -1) {
          scopes = [...scopes, DirectoryScopes.user];
        }
        if (state.item.groupsEnabled && scopes?.indexOf(DirectoryScopes.group) === -1) {
          scopes = [...scopes, DirectoryScopes.group];
        }
        if (state.domainAdded && scopes?.indexOf(DirectoryScopes.domain) === -1) {
          scopes = [...scopes, DirectoryScopes.domain];
        }
        return scopes;
      },

      displayName(state = { item: {} }) {
        const itemType = DirectoryTypeConsts[directoryType];

        if (directoryType === gSuite || directoryType === office365) {
          return state.item.name || DirectoryNameConsts[itemType];
        }

        return ItemTypeDisplayNames[itemType];
      },

      haveExportAttributesChanged(state) {
        const { exportAttributes, previousExportAttributes } = state;

        return Object.keys(exportAttributes)
          .filter(key => exportAttributes[key].value !== previousExportAttributes[key].value)
          .length > 0;
      },

      getExportAttributesKbUrl() {
        return exportAttributesKbUrl;
      },
    },

    mutations: {
      ...model.mutations,

      RESET_ITEM(state) {
        model.mutations.RESET_ITEM(state);
        Vue.set(state, 'exportAttributes', getDefaultExportAttributes());
        Vue.set(state, 'previousExportAttributes', getDefaultExportAttributes());
      },

      SET_DEFAULT_DOMAIN_ATTRIBUTE(state, defaultDomainIndex) {
        Vue.set(state.item, 'defaultDomainIndex', defaultDomainIndex);
      },

      SET_DOMAIN_ATTRIBUTE(state, { updatedDomainSelection }) {
        Vue.set(state.item, 'newlyAddedDomain', { updatedDomainSelection });
      },

      SET_DOMAIN_ENABLED(state, domainAdded) {
        Vue.set(state, 'domainAdded', domainAdded);
      },

      SET_SCOPES(state, scopes) {
        Vue.set(state, 'scopes', scopes);
      },

      SET_STATE(state, currentState) {
        Object.entries(currentState)
          .forEach(([key, value]) => {
            if (typeof value === 'boolean') {
              Vue.set(state, key, value);
            } else {
              Vue.set(state, key, { ...value });
            }
          },
          );
      },

      SET_EXPORT_ATTRIBUTE(state, {
        key, value, id, direction,
      }) {
        if (state.exportAttributes[key]) {
          Vue.set(state.exportAttributes[key], 'value', value);

          if (id) {
            Vue.set(state.exportAttributes[key], 'id', id);
          }
          if (direction) {
            Vue.set(state.exportAttributes[key], 'direction', direction);
          }
        }
      },

      SET_PREVIOUS_EXPORT_ATTRIBUTE(state, {
        key, value, id, direction,
      }) {
        if (state.previousExportAttributes[key]) {
          Vue.set(state.previousExportAttributes[key], 'value', value);

          if (id) {
            Vue.set(state.previousExportAttributes[key], 'id', id);
          }
          if (direction) {
            Vue.set(state.previousExportAttributes[key], 'direction', direction);
          }
        }
      },

      SET_PREVIOUS_GROUPSENABLED(state, groupsEnabled) {
        Vue.set(state, 'previousGroupsEnabled', groupsEnabled);
      },
    },
  };
}
